import React, { useEffect, useState } from "react";
import LibraryFormModal from "../components/LibraryFormModal";
import LibraryField, {
  libraryInputClass,
  librarySelectClass,
} from "../components/LibraryField";
import { ChevronDownIcon } from "../../../Icons";
import { PECOS_STATUS_OPTIONS } from "../../../services/librariesCatalogService";

const EMPTY = {
  firstName: "",
  lastName: "",
  address: "",
  npiNumber: "",
  phone: "",
  fax: "",
  pecosStatus: "un-enrolled",
  status: "Active",
};

const PhysicianFormModal = ({ isOpen, record, onClose, onSave }) => {
  const isEdit = Boolean(record?.id);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaving(false);
    setForm(
      record
        ? {
            firstName: record.firstName || "",
            lastName: record.lastName || "",
            address: record.address || "",
            npiNumber: record.npiNumber || "",
            phone: record.phone || "",
            fax: record.fax || "",
            pecosStatus: record.pecosStatus || "un-enrolled",
            status: record.status || "Active",
          }
        : EMPTY,
    );
  }, [isOpen, record]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = "First name is required";
    if (!form.lastName.trim()) next.lastName = "Last name is required";
    if (!/^\d{10}$/.test(form.npiNumber.trim())) {
      next.npiNumber = "NPI must be exactly 10 digits";
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      await onSave?.({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        address: form.address.trim(),
        npiNumber: form.npiNumber.trim(),
        phone: form.phone.trim(),
        fax: form.fax.trim(),
        pecosStatus: form.pecosStatus,
        status: form.status,
      });
      onClose?.();
    } catch (err) {
      setErrors({ form: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LibraryFormModal
      isOpen={isOpen}
      title={isEdit ? "Edit Physician" : "Create New Physician"}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSaving={saving}
      submitLabel={isEdit ? "Update" : "Create"}
    >
      {errors.form ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {errors.form}
        </p>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField
          id="phys-first"
          label="First Name"
          required
          error={errors.firstName}
        >
          <input
            id="phys-first"
            className={libraryInputClass(errors.firstName)}
            value={form.firstName}
            onChange={(e) => setField("firstName", e.target.value)}
            maxLength={100}
            autoFocus
          />
        </LibraryField>
        <LibraryField
          id="phys-last"
          label="Last Name"
          required
          error={errors.lastName}
        >
          <input
            id="phys-last"
            className={libraryInputClass(errors.lastName)}
            value={form.lastName}
            onChange={(e) => setField("lastName", e.target.value)}
            maxLength={100}
          />
        </LibraryField>
      </div>
      <LibraryField id="phys-address" label="Address">
        <input
          id="phys-address"
          className={libraryInputClass()}
          value={form.address}
          onChange={(e) => setField("address", e.target.value)}
          maxLength={500}
        />
      </LibraryField>
      <LibraryField
        id="phys-npi"
        label="NPI"
        required
        error={errors.npiNumber}
        hint="10 digits — synced to physician master for demographics search"
      >
        <input
          id="phys-npi"
          className={libraryInputClass(errors.npiNumber)}
          value={form.npiNumber}
          onChange={(e) =>
            setField("npiNumber", e.target.value.replace(/\D/g, "").slice(0, 10))
          }
          inputMode="numeric"
          maxLength={10}
        />
      </LibraryField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField id="phys-phone" label="Phone">
          <input
            id="phys-phone"
            className={libraryInputClass()}
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
          />
        </LibraryField>
        <LibraryField id="phys-fax" label="Fax">
          <input
            id="phys-fax"
            className={libraryInputClass()}
            value={form.fax}
            onChange={(e) => setField("fax", e.target.value)}
          />
        </LibraryField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField id="phys-pecos" label="PECOS">
          <div className="relative">
            <select
              id="phys-pecos"
              className={librarySelectClass()}
              value={form.pecosStatus}
              onChange={(e) => setField("pecosStatus", e.target.value)}
            >
              {PECOS_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
          </div>
        </LibraryField>
        <LibraryField id="phys-status" label="Status">
          <div className="relative">
            <select
              id="phys-status"
              className={librarySelectClass()}
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            <ChevronDownIcon
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
          </div>
        </LibraryField>
      </div>
    </LibraryFormModal>
  );
};

export default PhysicianFormModal;
